/**
 * Vote en ligne — l'isoloir de l'élève, sur son propre appareil.
 *
 * Distinct de `view-isoloir`, qui sert le mode kiosque sur l'appareil de
 * l'enseignant : ici, il n'y a aucun journal local, seulement un état public
 * rapatrié du relais et un bulletin déposé.
 *
 * L'élève ne transmet jamais son code en clair : il calcule localement
 * l'empreinte de son jeton à partir du sel publié, et n'envoie que celle-ci.
 */

import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { CODE_LENGTHS, isValidCode, normalizeCode, randomId, tokenHash } from '../../core/index.js';
import {
  baseApi,
  deposerBulletin,
  ErreurRelais,
  lireEtat,
  resoudreCode,
  signalerPresence,
  verifierCodeLiaison,
  type EtatPublic,
} from '../../transport/distant.js';
import { StoreElement } from '../base.js';
import { pluriel } from '../format.js';
import { icone } from '../icons.js';
import { valeurDe } from '../md.js';

type Etape = 'liaison' | 'code' | 'vote' | 'confirme';

const CADENCE_MS = 15_000;

/**
 * Messages du premier écran, quand la saisie est en fait un autre code.
 *
 * Chaque cas explique **ce qui a été saisi** et **où saisir le bon code** :
 * « code inconnu » laisserait l'élève essayer le même papier indéfiniment.
 */
const MESSAGE_LIAISON: Record<'personnel' | 'enseignant' | 'forme', string> = {
  personnel:
    'C’est votre code personnel, pas le code de l’élection. Ici, saisissez le code affiché par votre professeur, du type ABC-123 : votre code personnel se saisira à l’écran suivant.',
  enseignant:
    'C’est le code enseignant : il ne sert pas à voter. Saisissez le code de l’élection affiché par votre professeur, du type ABC-123.',
  forme:
    'Le code de l’élection compte 6 caractères, du type ABC-123. Il est affiché ou vidéoprojeté par votre professeur — ce n’est pas votre code personnel.',
};

/** Identifiant opaque de l'appareil, pour le compteur de présence du professeur. */
const CLE_CLIENT = 'election.nsi.xyz/v1/client';

@customElement('view-vote')
export class ViewVote extends StoreElement {
  @state() private etape: Etape = 'liaison';
  @state() private codeLiaison = '';
  @state() private electionId: string | null = null;
  @state() private etat: EtatPublic | null = null;
  @state() private code = '';
  @state() private choix: string[] = [];
  @state() private erreur = '';
  @state() private occupe = false;
  @state() private chargement = false;

  private minuteur: ReturnType<typeof setInterval> | null = null;
  private etiquette: string | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    const identifiant = this.#identifiantDansUrl();
    if (identifiant !== null) {
      this.electionId = identifiant;
      this.etape = 'code';
      void this.chargerEtat();
    }
    void this.signalerAuRelais();
    this.minuteur = setInterval(() => {
      void this.chargerEtat();
      void this.signalerAuRelais();
    }, CADENCE_MS);
  }

  override disconnectedCallback(): void {
    if (this.minuteur !== null) clearInterval(this.minuteur);
    this.minuteur = null;
    super.disconnectedCallback();
  }

  /** Identifiant stable de l'onglet : le professeur ne voit qu'un compteur. */
  #clientId(): string {
    try {
      const existant = sessionStorage.getItem(CLE_CLIENT);
      if (existant !== null) return existant;
    } catch {
      // Stockage indisponible : l'appareil sera recompté, sans conséquence.
    }
    const id = randomId('cli');
    try {
      sessionStorage.setItem(CLE_CLIENT, id);
    } catch {
      // ignoré
    }
    return id;
  }

  private async signalerAuRelais(): Promise<void> {
    if (this.electionId === null) return;
    await signalerPresence(this.electionId, this.#clientId());
  }

  #identifiantDansUrl(): string | null {
    const brut = location.hash.replace(/^#\/?/, '');
    const [, identifiant] = brut.split('/');
    return identifiant !== undefined && identifiant.length > 0 ? identifiant : null;
  }

  /** Rapatrie l'état public. Une réponse 304 ne coûte presque rien. */
  private async chargerEtat(): Promise<void> {
    if (this.electionId === null || this.chargement) return;
    this.chargement = true;
    try {
      const reponse = await lireEtat(this.electionId, this.etiquette);
      if (reponse.inchange) return;
      this.etiquette = reponse.etiquette;
      if (reponse.etat !== null) this.etat = reponse.etat;
      this.erreur = '';
    } catch {
      this.erreur = 'Le serveur de vote est injoignable. Vérifiez votre connexion, puis réessayez.';
    } finally {
      this.chargement = false;
    }
  }

  private async rejoindre(): Promise<void> {
    const verdict = verifierCodeLiaison(this.codeLiaison);
    if (!verdict.ok) {
      this.erreur = MESSAGE_LIAISON[verdict.motif];
      return;
    }
    this.occupe = true;
    try {
      const electionId = await resoudreCode(verdict.code);
      this.electionId = electionId;
      location.hash = `#/vote/${electionId}`;
      this.etape = 'code';
      this.erreur = '';
      void this.signalerAuRelais();
      await this.chargerEtat();
    } catch (erreur) {
      this.erreur =
        erreur instanceof ErreurRelais && erreur.statut === 404
          ? 'Ce code d’élection est inconnu. Vérifiez le code affiché par votre professeur — et si l’élection n’est pas encore publiée, scannez le QR code ou attendez son ouverture.'
          : erreur instanceof Error
            ? erreur.message
            : 'Le serveur de vote est injoignable.';
    } finally {
      this.occupe = false;
    }
  }

  private validerCode(): void {
    const propre = normalizeCode(this.code);
    if (!isValidCode(propre, CODE_LENGTHS.eleve)) {
      this.erreur = 'Ce code est incomplet ou mal recopié. Vérifiez les 9 caractères imprimés sur votre papier.';
      return;
    }
    this.erreur = '';
    this.etape = 'vote';
    void this.chargerEtat();
  }

  private basculer(itemId: string): void {
    if (this.choix.includes(itemId)) {
      this.choix = this.choix.filter((id) => id !== itemId);
      return;
    }
    if (this.choix.length >= this.voixDisponibles) return;
    this.choix = [...this.choix, itemId];
  }

  private get tour(): 1 | 2 {
    return this.etat?.phase === 'TOUR2' ? 2 : 1;
  }

  private get voixDisponibles(): number {
    return Math.max(0, this.etat?.voixDisponibles ?? 0);
  }

  private async deposer(blanc: boolean): Promise<void> {
    if (this.electionId === null || this.etat === null) return;
    this.occupe = true;
    try {
      const empreinte = tokenHash(this.code, this.etat.salt);
      const resultat = await deposerBulletin(this.electionId, {
        tokenHash: empreinte,
        choices: blanc ? [] : this.choix,
        blank: blanc,
        round: this.tour,
      });
      if (resultat === 'ok') {
        this.etape = 'confirme';
        return;
      }
      this.etape = 'code';
      this.choix = [];
      this.erreur =
        resultat === 'double'
          ? 'Ce code a déjà voté pour ce tour. Un bulletin est définitif.'
          : resultat === 'inconnu'
            ? 'Ce code n’appartient pas à cette élection. Adressez-vous à votre professeur.'
            : resultat === 'occupe'
              ? 'Le serveur est très sollicité. Attendez quelques secondes, puis réessayez.'
              : 'Le scrutin n’est pas ouvert à cet instant.';
    } catch {
      this.erreur = 'Le bulletin n’a pas pu être déposé. Vérifiez votre connexion, puis réessayez.';
    } finally {
      this.occupe = false;
    }
  }

  private suivant(): void {
    this.etape = 'code';
    this.code = '';
    this.choix = [];
    this.erreur = '';
  }

  override render() {
    const phase = this.etat?.phase ?? null;
    const scrutinOuvert = phase === 'TOUR1' || phase === 'TOUR2';

    return html`
      <div class="page page--narrow">
        <div class="row row--between">
          <div class="stack stack--tight">
            <span class="eyebrow">${icone('vote', 17)} Vote en ligne</span>
            <h1 class="t-headline">${this.etat?.nom ?? 'Élection des délégués'}</h1>
            ${this.etat?.classe !== undefined ? html`<p class="t-body t-muted">${this.etat.classe}</p>` : nothing}
          </div>
          <md-text-button @click=${() => (location.hash = '#/')}>Quitter</md-text-button>
        </div>

        ${this.erreur !== ''
          ? html`<section class="card card--warn">
              <div class="card__head">
                <span class="chip-icon chip-icon--alerte" aria-hidden="true">${icone('alerte', 22)}</span>
                <p class="t-body">${this.erreur}</p>
              </div>
            </section>`
          : nothing}

        ${this.#renderEtape(scrutinOuvert)}

        <p class="t-body t-muted t-center">
          Ce vote passe par le serveur de l’établissement. Votre nom n’y est jamais transmis : seul le vote est
          enregistré, sous forme d’une empreinte.
        </p>
      </div>
    `;
  }

  #renderEtape(scrutinOuvert: boolean) {
    if (this.etape === 'liaison') return this.#renderLiaison();
    if (this.etape === 'code') return this.#renderCode(scrutinOuvert);
    if (this.etape === 'vote') return this.#renderVote(scrutinOuvert);
    return this.#renderConfirmation();
  }

  #renderLiaison() {
    return html`
      <section class="card">
        <div class="card__head">
          <span class="card__step" aria-hidden="true">1</span>
          <h2 class="t-title">Code de l’élection</h2>
        </div>
        <p class="t-body t-muted">
          Il est vidéoprojeté ou affiché par votre professeur : 6 caractères, du type
          <span class="t-mono">ABC-123</span>. <strong>Ce n’est pas votre code personnel</strong> — celui-ci, à 9
          caractères, se saisit à l’écran suivant.
        </p>
        <input
          class="code-input"
          type="text"
          autocapitalize="characters"
          autocomplete="off"
          spellcheck="false"
          aria-label="Code de l’élection"
          placeholder="ABC-123"
          .value=${this.codeLiaison}
          @input=${(evenement: Event) => {
            this.codeLiaison = valeurDe(evenement);
            this.erreur = '';
          }}
          @keydown=${(evenement: KeyboardEvent) => {
            if (evenement.key === 'Enter') void this.rejoindre();
          }}
        />
        <div class="row row--end">
          <md-filled-button
            class="btn--big"
            ?disabled=${this.occupe || this.codeLiaison.trim().length === 0}
            @click=${() => void this.rejoindre()}
            >Continuer</md-filled-button
          >
        </div>
        <p class="t-body t-muted">
          Serveur : <span class="t-mono">${baseApi()}</span>
        </p>
      </section>
    `;
  }

  #renderCode(scrutinOuvert: boolean) {
    return html`
      <section class="card">
        <div class="card__head">
          <span class="card__step" aria-hidden="true">2</span>
          <h2 class="t-title">Votre code personnel</h2>
        </div>
        ${!scrutinOuvert
          ? html`<p class="t-body t-error">
              Le scrutin n’est pas ouvert à cet instant. Vous pourrez voter dès que votre professeur l’aura ouvert.
            </p>`
          : nothing}
        <p class="t-body t-muted">
          Il figure sur le papier remis par votre professeur. Le code est à usage unique et n’est jamais transmis :
          votre téléphone n’envoie qu’une empreinte.
        </p>
        <input
          class="code-input"
          type="text"
          inputmode="latin"
          autocapitalize="characters"
          autocomplete="off"
          spellcheck="false"
          aria-label="Code personnel"
          placeholder="XXXXX-XXXX"
          .value=${this.code}
          @input=${(evenement: Event) => {
            this.code = valeurDe(evenement);
            this.erreur = '';
          }}
          @keydown=${(evenement: KeyboardEvent) => {
            if (evenement.key === 'Enter') this.validerCode();
          }}
        />
        <div class="row row--end">
          <md-filled-button
            class="btn--big"
            ?disabled=${this.code.trim().length === 0 || !scrutinOuvert}
            @click=${() => this.validerCode()}
            >Continuer</md-filled-button
          >
        </div>
      </section>
    `;
  }

  #renderVote(scrutinOuvert: boolean) {
    const items = this.etat?.items ?? [];
    const complet = this.choix.length >= this.voixDisponibles;

    return html`
      <section class="card">
        <div class="card__head">
          <span class="card__step" aria-hidden="true">3</span>
          <h2 class="t-title">
            ${this.tour === 1 ? 'Premier tour' : 'Second tour'} — choisissez
            ${this.voixDisponibles === 1 ? 'un nom' : 'deux noms'}
          </h2>
        </div>

        <div class="row row--between">
          <span class="t-body t-muted">${pluriel(this.voixDisponibles, 'voix disponible', 'voix disponibles')}</span>
          <span class="progress-dots" aria-hidden="true">
            ${Array.from(
              { length: this.voixDisponibles },
              (_, index) =>
                html`<span class="progress-dots__point ${index < this.choix.length ? 'progress-dots__point--plein' : ''}"
                ></span>`,
            )}
          </span>
        </div>
        <p class="t-body t-muted">
          Vous pouvez voter pour un binôme — un candidat et son suppléant — ou pour une personne seule.
        </p>

        <div class="ballot" role="group" aria-label="Choix du bulletin">
          ${items.map((item) => {
            const choisi = this.choix.includes(item.id);
            return html`
              <button
                type="button"
                class="option ${choisi ? 'option--picked' : ''}"
                aria-pressed=${choisi ? 'true' : 'false'}
                ?disabled=${!choisi && complet}
                @click=${() => this.basculer(item.id)}
              >
                <span class="option__mark" aria-hidden="true">${choisi ? icone('check', 18) : nothing}</span>
                <span class="option__corps grow">
                  <span class="t-title">${item.label}</span>
                  <span class="option__kind">
                    ${item.kind === 'binome' ? 'Binôme — candidat et suppléant' : 'Candidature individuelle — sans suppléant'}
                  </span>
                </span>
              </button>
            `;
          })}
        </div>

        ${items.length === 0
          ? html`<p class="t-body t-muted">La liste des candidats n’est pas encore publiée.</p>`
          : nothing}

        <div class="row row--between actions-fixes">
          <md-outlined-button ?disabled=${this.occupe || !scrutinOuvert} @click=${() => void this.deposer(true)}
            >Vote blanc</md-outlined-button
          >
          <md-filled-button
            class="btn--big"
            ?disabled=${this.occupe || !scrutinOuvert || (!complet && this.choix.length === 0)}
            @click=${() => void this.deposer(false)}
            >Déposer mon bulletin</md-filled-button
          >
        </div>
        <p class="t-body t-muted">Le dépôt est définitif : aucun vote ne peut être modifié ensuite.</p>
      </section>
    `;
  }

  #renderConfirmation() {
    return html`
      <section class="card card--ok">
        <div class="card__head">
          <span class="chip-icon chip-icon--ok" aria-hidden="true">${icone('checkCercle', 24)}</span>
          <h2 class="t-title">Bulletin déposé</h2>
        </div>
        <p class="t-body">
          Votre vote est enregistré et reste secret : personne, pas même l’enseignant, ne peut savoir ce que vous avez
          choisi.
        </p>
        <div class="row row--end">
          <md-filled-button class="btn--big" @click=${() => this.suivant()}>Terminer</md-filled-button>
        </div>
      </section>
    `;
  }
}
